import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { isDiagnosticsDomain, isPreviewDomain } from '@/utils/runtimeNoisePolicy';

const USERBACK_ACCESS_TOKEN = 'A-05YaiYaMeBZwkI1uiBnvAGRmc1V5KH79PURiT3fAcAdE06sUsl';
export const USERBACK_FEEDBACK_URL = 'https://productfeedbackandroadmap50941.ideas.userback.io/p/a3PE8v1ToSjZZ8QLmkWY';

export function isMobileEditorPath(pathname: string): boolean {
  return pathname === '/m' || pathname === '/m/new' || pathname.startsWith('/m/');
}

export function suppressUserbackLauncher(suppressed: boolean): void {
  if (typeof window === 'undefined') return;
  const userback = (window as any).Userback;
  try {
    if (suppressed && userback && typeof userback.hide === 'function') {
      userback.hide();
    } else if (!suppressed && userback && typeof userback.show === 'function') {
      userback.show();
    }
  } catch {
    void 0;
  }
}

export function openUserbackFeedback(): void {
  if (typeof window === 'undefined') return;
  const userback = (window as any).Userback;
  if (userback && typeof userback.open === 'function') {
    try {
      userback.open();
      return;
    } catch {
      void 0;
    }
  }
  window.open(USERBACK_FEEDBACK_URL, '_blank', 'noopener,noreferrer');
}

export function UserbackRouteSuppressor() {
  const location = useLocation();
  useEffect(() => {
    suppressUserbackLauncher(isMobileEditorPath(location.pathname));
  }, [location.pathname]);
  return null;
}

export default function UserbackWidget() {
  const { user } = useAuth();
  const location = useLocation();
  const [scriptFailed, setScriptFailed] = useState(false);
  const allowDiagnosticsUserback = import.meta.env.VITE_ENABLE_USERBACK_ON_DIAGNOSTICS === 'true';
  const shouldLoadUserback = allowDiagnosticsUserback || isPreviewDomain() || !isDiagnosticsDomain();
  const mobileRoute = isMobileEditorPath(location.pathname);

  useEffect(() => {
    suppressUserbackLauncher(mobileRoute);
  }, [mobileRoute]);

  useEffect(() => {
    if (!shouldLoadUserback || typeof window === 'undefined') {
      return;
    }

    const applyUserData = () => {
      if (!(window as any).Userback || !user) {
        return;
      }
      (window as any).Userback.user_data = {
        id: user.id,
        info: {
          name: user.user_metadata?.name || user.email?.split('@')[0] || 'User',
          email: user.email || '',
        },
      };
    };

    // On mobile editor routes we still LOAD the widget (so the toolbar Feedback
    // button can call Userback.open() and show the in-app overlay) but keep the
    // floating launcher hidden so it doesn't take up screen space.
    const hideLauncherIfMobile = () => {
      if (mobileRoute) suppressUserbackLauncher(true);
    };

    if ((window as any).Userback) {
      setScriptFailed(false);
      applyUserData();
      hideLauncherIfMobile();
      return;
    }

    (window as any).Userback = (window as any).Userback || {};
    (window as any).Userback.access_token = USERBACK_ACCESS_TOKEN;
    applyUserData();

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://static.userback.io/widget/v1.js';
    script.onload = () => {
      const userback = (window as any).Userback;
      if (!userback || typeof userback !== 'object') {
        console.warn('[Userback] Script loaded but widget API was not initialized');
        setScriptFailed(true);
        return;
      }
      setScriptFailed(false);
      applyUserData();
      hideLauncherIfMobile();
    };
    script.onerror = () => {
      console.warn('[Userback] Failed to load widget script');
      setScriptFailed(true);
    };
    (document.head || document.body).appendChild(script);

    const healthCheckTimer = window.setTimeout(() => {
      const userback = (window as any).Userback;
      if (!userback || typeof userback.open !== 'function') {
        console.warn('[Userback] Widget health check failed: open() is unavailable');
        setScriptFailed(true);
      }
    }, 5000);

    return () => {
      window.clearTimeout(healthCheckTimer);
    };
  }, [mobileRoute, shouldLoadUserback, user]);

  if (!shouldLoadUserback || mobileRoute || !scriptFailed) {
    return null;
  }

  return (
    <a
      href={USERBACK_FEEDBACK_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-4 right-4 z-[80] inline-flex items-center gap-2 rounded-full border border-sl-surface-2 bg-sl-surface px-3 py-2 text-xs text-sl-text shadow-lg hover:bg-sl-surface-2 transition-colors"
      aria-label="Send feedback"
    >
      <i className="fas fa-comment-dots" aria-hidden="true" />
      <span>Send Feedback</span>
    </a>
  );
}
